import { useMemo } from 'react'

import {
  useCreateDocFolderMutation,
  useListDocFoldersQuery,
  useListDocsQuery,
  type ChatArtifact,
  type Doc
} from '@axilon-apps/domains-ontology-ui-state'

import { downloadName, withMarkdownSuffix } from '../DocsHubPage/doc-helpers'
import { isIngestedDocumentsPath } from '../DocsHubPage/ingested-documents-virtual'
import { documentArtifactBody } from './document-artifact'
import SaveArtifactDialog from './SaveArtifactDialog'

interface SaveToDocsHubDialogProps {
  open: boolean
  artifact: ChatArtifact
  onClose: () => void
  onSaved: (doc: Doc) => void
}

export default function SaveToDocsHubDialog({
  open,
  artifact,
  onClose,
  onSaved
}: SaveToDocsHubDialogProps) {
  const foldersQuery = useListDocFoldersQuery()
  const docsQuery = useListDocsQuery()
  const [createDocFolder] = useCreateDocFolderMutation()
  const body = documentArtifactBody(artifact)
  const toItemName =
    body.kind === 'file'
      ? (name: string) => downloadName(name.trim(), body.format)
      : withMarkdownSuffix

  // Ingested Documents is read-only, so the picker leaves its subtree out.
  const folders = useMemo(
    () => (foldersQuery.data ?? []).filter((folder) => !isIngestedDocumentsPath(folder.path)),
    [foldersQuery.data]
  )
  const docs = useMemo(
    () => (docsQuery.data ?? []).filter((doc) => !isIngestedDocumentsPath(doc.path)),
    [docsQuery.data]
  )

  return (
    <SaveArtifactDialog
      open={open}
      artifact={artifact}
      library="Documents"
      itemNoun="doc"
      description="Choose where this document should appear in Documents."
      idPrefix="artifact-doc"
      folders={folders}
      items={docs}
      createFolder={(input) => createDocFolder(input).unwrap()}
      toItemName={toItemName}
      onClose={onClose}
      onSaved={(saved) => onSaved(saved as Doc)}
    />
  )
}
